// Pure z-order resolver for the scene canvas. Given the authored box-id order
// and each box's resolved startFrame (frames, or `undefined` for a gate-less
// box with no entry timing — always visible), returns the ids sorted so
// that, AT THE CURRENT PLAYHEAD FRAME, the layer that began most recently
// sits frontmost (last in the array), layers not yet begun stay behind, and
// ties keep the authored `.boxes` order. See EditorCanvasSli for why
// ordering must be playhead-aware (frame 0 = scene-start layer on top;
// mid-scene overlap = the newly-revealed layer on top).
//
// Recency only arbitrates between two GATED layers that are explicitly linked
// as the SAME reveal group (shared `timeline.splitGroup` — today, the two
// halves of a split video clip; `splitGroupById` returns undefined/null for
// everything else). Any other pair of boxes — including two unrelated boxes
// that both merely happen to have a numeric `timeline.startFrame` — never
// competes on recency and keeps its authored `.boxes` position.
//
// This is deliberately narrow: `timeline.startFrame` is set by several
// unrelated mechanisms (a user dragging the layer bar, "insert at playhead"
// bookkeeping on ANY new element, screen-training import, …), so its mere
// presence says nothing about whether two boxes are meant to duel for
// stacking order. Without the splitGroup guard, an explicitly "brought to
// front" image/heading/etc. gets buried the instant ANY other, unrelated
// element's bookkeeping startFrame is reached during playback — see
// docs/knowledge/book or CU-86eyrmuw5 history for the incident this guards
// against. Split clip parts are the one case that both (a) genuinely need
// the later piece on top and (b) are unambiguously linked by data, not by
// coincidence.
export function orderBoxesByPlayhead(boxIds, startFrameById, nowFrame, splitGroupById) {
  const groupOf = typeof splitGroupById === "function" ? splitGroupById : () => undefined;
  return (boxIds || [])
    .map((id, i) => [id, startFrameById(id), i, groupOf(id)])
    .sort((a, b) => {
      const aGate = typeof a[1] === "number";
      const bGate = typeof b[1] === "number";
      const aStarted = aGate ? a[1] <= nowFrame : true;
      const bStarted = bGate ? b[1] <= nowFrame : true;
      if (aStarted !== bStarted) return aStarted ? 1 : -1; // started layers to the front
      const sameRevealGroup = a[3] != null && a[3] === b[3];
      if (aStarted && aGate && bGate && sameRevealGroup && a[1] !== b[1]) return a[1] - b[1]; // later start = frontmost, within the same reveal group only
      return a[2] - b[2]; // stable: authored order
    })
    .map((t) => t[0]);
}
